import { useMemo } from 'react';
import { Formik, Form } from 'formik';

import { Widget, WidgetBody } from '@@/Widget';

import { TypedPolicy } from '../../../types';
import { useNameValidation } from '../../../validation/useNameValidation';
import { PolicyConfigurationFormSection } from '../../PolicyConfigurationFormSection';
import { PolicyFormFooter } from '../../PolicyFormFooter';

import { DockerCleanupFormFields } from './DockerCleanupFormFields';
import { DockerCleanupFormValues } from './types';
import { useDockerCleanupInitialValues } from './useDockerCleanupInitialValues';
import { createDockerCleanupValidationSchema } from './dockerCleanupValidationSchema';

interface Props {
  onSubmit: (values: DockerCleanupFormValues) => Promise<void>;
  mode: 'create' | 'edit';
  policy?: TypedPolicy;
  templateData?: Record<string, unknown>;
  onBack?: () => void;
  canGoBack?: boolean;
}

export function DockerCleanupPolicyForm({
  onSubmit,
  mode,
  policy,
  templateData,
  onBack,
  canGoBack,
}: Props) {
  const initialValues = useDockerCleanupInitialValues(policy, templateData);
  const originalEnvironmentGroupIds = policy?.EnvironmentGroups || [];
  const nameValidation = useNameValidation(policy?.Id);
  const validationSchema = useMemo(
    () => createDockerCleanupValidationSchema(nameValidation),
    [nameValidation]
  );

  return (
    <Formik<DockerCleanupFormValues>
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      enableReinitialize={mode === 'edit'}
      validateOnMount
    >
      <Form className="form-horizontal">
        <Widget>
          <WidgetBody>
            {mode === 'create' && (
              <PolicyConfigurationFormSection policyType="cleanup-docker" />
            )}
            <DockerCleanupFormFields />
            {mode === 'edit' && (
              <PolicyFormFooter
                mode={mode}
                policyType="cleanup-docker"
                policyId={policy?.Id ?? null}
                originalEnvironmentGroupIds={originalEnvironmentGroupIds}
              />
            )}
          </WidgetBody>
        </Widget>

        {mode === 'create' && (
          <PolicyFormFooter
            mode={mode}
            policyType="cleanup-docker"
            policyId={policy?.Id ?? null}
            onBack={onBack}
            canGoBack={canGoBack}
          />
        )}
      </Form>
    </Formik>
  );
}
